/*
 * Text for the trial page: dates, the slug preview, and the email draft.
 * Plain functions, no React, so the email can be checked without rendering.
 */

export const REGION_LABELS = { US: 'US', EU: 'EU', APAC: 'APAC' };
export const REGION_ORDER = ['US', 'EU', 'APAC'];

/** The last second of a calendar day, local time — a trial runs *through* its end date. */
export const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/** Whole calendar days from today to `end` (today + 14 at 23:59 -> 14). */
export const daysUntil = (end, from = new Date()) => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((b - a) / 86400000);
};

export const fmtLongDate = (d) =>
  new Date(d).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

export const fmtShortDate = (d) =>
  new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export const fmtTime = (d) =>
  new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

/**
 * Preview only — the server builds the real names with the same rules
 * (server/services/trial/trialNames.service.js).
 */
export const previewSlug = (name) => {
  const slug = String(name || '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug.length <= 24) return slug;
  const cut = slug.slice(0, 25);
  const i = cut.lastIndexOf('-');
  return (i >= 8 ? cut.slice(0, i) : slug.slice(0, 24)).replace(/-+$/g, '');
};

const signInUrl = (loginUrl) => (loginUrl ? `${loginUrl.replace(/\/+$/, '')}/login` : '[sign-in link]');

export const ENV_LABELS = { dev: 'Dev', staging: 'Staging', prod: 'Production' };
export const envLabel = (env) => ENV_LABELS[env] || env;

/** "Staging · EU" — the environment is only worth naming when a trial spans several. */
export const partTitle = (part, trial) =>
  (trial.environments || []).length > 1 ? `${envLabel(part.env)} · ${REGION_LABELS[part.region]}` : REGION_LABELS[part.region];

/**
 * Accounts grouped by where they sign in. Parts that share a sign-in address are
 * listed under it once; each server's address comes from that server.
 */
export const accountsBlock = (trial) => {
  const bySite = new Map();
  for (const p of trial.parts || []) {
    const url = signInUrl(p.loginUrl);
    if (!bySite.has(url)) bySite.set(url, []);
    bySite.get(url).push(p);
  }
  const multipleSites = bySite.size > 1;
  return [...bySite.entries()]
    .map(([url, parts]) => {
      const lists = parts
        .map((p) => `${partTitle(p, trial)} accounts (${p.emails.length})\n${p.emails.map((e, i) => `  ${i + 1}. ${e}`).join('\n')}`)
        .join('\n\n');
      return multipleSites ? `Sign in at ${url}\n\n${lists}` : lists;
    })
    .join('\n\n');
};

/** One sign-in address for the whole trial, or none when accounts sit on several sites. */
const singleSignInUrl = (trial) => {
  const urls = [...new Set((trial.parts || []).map((p) => signInUrl(p.loginUrl)))];
  return urls.length === 1 ? urls[0] : null;
};

/** Accounts plus the password — what "Copy accounts" puts on the clipboard. */
export const credentialsBlock = (trial, password) =>
  `${accountsBlock(trial)}\n\nTemporary password (same for every account): ${password || '[password]'}`;

export const emailSubject = (trial) => `Your LavenirAI trial access for ${trial.companyName}`;

/** Signs every trial email; also the sender name it goes out under. */
export const EMAIL_SIGNATURE = 'LavenirAI Support';

/**
 * The trial email: neutral and factual — what the accounts are, until when, and
 * how to sign in. Without a password (a trial listed later, whose password was
 * not stored) it leaves a [password] placeholder.
 */
export const emailBody = (trial, password) => {
  const end = new Date(trial.expiresAt);
  const count = trial.accountCount;
  const url = singleSignInUrl(trial);
  return [
    'Hello,',
    '',
    `Your LavenirAI trial for ${trial.companyName} is ready: ${count} account${count === 1 ? '' : 's'}.`,
    '',
    `Access ends: ${fmtLongDate(end)} at ${fmtTime(end)}`,
    url ? `Sign in at: ${url}` : 'Sign in at the address shown above each group of accounts.',
    `Temporary password (same for every account): ${password || '[password]'}`,
    '',
    accountsBlock(trial),
    '',
    EMAIL_SIGNATURE,
  ].join('\n');
};
